/**
 * Pure date and sort utility functions for filters.
 * Extracted from filters barrel to avoid importing ThirdPartyConfigs.
 *
 */

import { SORT_FIELD_KEY, SORT_ORDER } from '@atlassian/search-client/results-sort';

import { Sort } from '../../constants/schemas/query-params';

export const convertCustomDateToRange: (
	from?: string,
	to?: string,
) => {
	gt: string | undefined;
	lt: string | undefined;
} = (from?: string, to?: string) => {
	const fromDate = from ? new Date(from) : new Date();
	const startOfTheFromDate = new Date(
		fromDate.getFullYear(),
		fromDate.getMonth(),
		fromDate.getDate(),
		0,
		0,
		0,
		0,
	);
	const toDate = to ? new Date(to) : new Date();
	const endOfTheToDate = new Date(
		toDate.getFullYear(),
		toDate.getMonth(),
		toDate.getDate(),
		23,
		59,
		59,
		999,
	);
	return {
		gt: from ? startOfTheFromDate.toISOString().split('Z')[0] : undefined,
		lt: to ? endOfTheToDate.toISOString().split('Z')[0] : undefined,
	};
};

// Copied from Confluence advanced-search package
// generates truncated iso strings for Confluence last modified filter defaults (today, yesterday, etc)
// considering the user's locale-based definition of start OR end of day
export const getUTCDateRange: (
	fromOffset: number,
	toOffset: number,
) => {
	gt: string;
	lt: string;
} = (fromOffset: number, toOffset: number) => {
	const currentDate = new Date();
	return {
		gt: new Date(
			new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()).getTime() -
				fromOffset * 24 * 60 * 60 * 1000,
		)
			.toISOString()
			.split('Z')[0],
		lt: new Date(
			new Date(
				currentDate.getFullYear(),
				currentDate.getMonth(),
				currentDate.getDate(),
				23,
				59,
				59,
				999,
			).getTime() -
				toOffset * 24 * 60 * 60 * 1000,
		)
			.toISOString()
			.split('Z')[0],
	};
};

export const convertLastUpdatedToDateRange: (lastUpdated: string) => {
	gt: string;
	lt: string;
} = (lastUpdated: string) => {
	let dateRangeValues;
	switch (lastUpdated) {
		case 'today':
			dateRangeValues = getUTCDateRange(0, 0);
			break;
		case 'yesterday':
			dateRangeValues = getUTCDateRange(1, 1);
			break;
		case 'past7Days':
			dateRangeValues = getUTCDateRange(7, 0);
			break;
		case 'past30Days':
			dateRangeValues = getUTCDateRange(30, 0);
			break;
		case 'pastYear':
			dateRangeValues = getUTCDateRange(365, 0);
			break;
		default:
			dateRangeValues = { gt: '', lt: '' };
	}
	return dateRangeValues;
};

export const convertSortToFilter = (value: string) => {
	switch (value) {
		case Sort.Relevance:
			return [];
		case Sort.UpdatedNewest:
			return [{ field: SORT_FIELD_KEY.LAST_MODIFIED_DATE, order: SORT_ORDER.DESC }];
		case Sort.UpdatedOldest:
			return [{ field: SORT_FIELD_KEY.LAST_MODIFIED_DATE, order: SORT_ORDER.ASC }];
		case Sort.CreatedNewest:
			return [{ field: SORT_FIELD_KEY.CREATED_DATE, order: SORT_ORDER.DESC }];
		case Sort.CreatedOldest:
			return [{ field: SORT_FIELD_KEY.CREATED_DATE, order: SORT_ORDER.ASC }];
		default:
			return [];
	}
};

/**
 * Gets a UTC date string (YYYY-MM-DD) for a given number of days ago.
 * Uses UTC timezone to match backend date calculations.
 */

export const getConfluenceDateOptionFromCustomDates: (
	from: string,
	to: string,
) => import('@atlassian/search-client').ConfluenceRangeFilter = (from: string, to: string): import('@atlassian/search-client').ConfluenceRangeFilter => {
	const { gt, lt } = convertCustomDateToRange(from, to);
	return {
		field: 'LASTMODIFIED',
		gt,
		lt,
	};
};

export const getConfluenceDateOptionValues: (
	selectedDateOption: string,
) => import('@atlassian/search-client').ConfluenceRangeFilter = (selectedDateOption: string): import('@atlassian/search-client').ConfluenceRangeFilter => {
	const dateRangeValues = convertLastUpdatedToDateRange(selectedDateOption);
	const { gt, lt } = dateRangeValues;
	return {
		field: 'LASTMODIFIED',
		gt,
		lt,
	};
};
